import ExpiresAfter from "domain/ExpiresAfter";
import {type DateLike, formatDate, formatDateTimeMed} from "core/format/dateFormat";
import type {Locale} from "i18n/localePreference";
import {expiryDays, expiryMinutes, isIndefiniteExpiry, isSubDayExpiry} from "./gating";

/**
 * The ONE label for a chosen retention (kitecorp/transferify#90).
 *
 * Three call sites formatted this independently before — the create picker, the extend sheet and the
 * success card — and only the first of them branched on the indefinite rung, so the other two were one
 * PREMIUM transfer away from rendering "NaN days". The sub-day ladder would have added a fourth shape to
 * get wrong in three places, so the rule moved here and the views ask for it.
 *
 * React-free (`logic-outside-react`): the caller passes the catalog slice, exactly as
 * `download/expiry.ts` takes its `CountdownCopy`. The shape is declared structurally rather than as
 * `Messages["sender"]` so this module never reaches into the catalog (which pulls in JSX bodies).
 */
export interface ExpiryLabelCopy {
	/** "1 day" / "30 days" — the day ladder. */
	expiryDays: (days: number) => string;
	/** "15 minutes" — sub-day rungs under an hour. */
	expiryMinutes: (minutes: number) => string;
	/** "2 hours" — sub-day rungs of an hour or more. */
	expiryHours: (hours: number) => string;
	/** The indefinite rung, which has no count at all. */
	expiryNever: string;
}

/** Minutes in an hour — the boundary between the two sub-day copy shapes. */
const MINUTES_PER_HOUR = 60;

/**
 * The localized label for `value`, in whichever unit the sender actually chose.
 *
 * A sub-day rung is NEVER labelled in days: its bytes ride the api's 1-day storage floor, but the SENDER
 * picked 15 minutes and that is what the label must say.
 */
export function expiryLabelOf(copy: ExpiryLabelCopy, value: ExpiresAfter): string {
	if (isIndefiniteExpiry(value)) {
		return copy.expiryNever;
	}
	if (!isSubDayExpiry(value)) {
		return copy.expiryDays(expiryDays(value));
	}
	const minutes = expiryMinutes(value);
	return minutes < MINUTES_PER_HOUR
		? copy.expiryMinutes(minutes)
		: copy.expiryHours(minutes / MINUTES_PER_HOUR);
}

/**
 * An expiry INSTANT written at the precision the sender's chosen rung deserves: a date for a day-based
 * retention, a date AND time for a sub-day one (kitecorp/transferify#90).
 *
 * One function rather than the branch repeated at each site, for the same reason {@link expiryLabelOf}
 * exists: the owner's transfer list, the extend sheet's current expiry and the extend sheet's target
 * chips all answer this question, and the list was still printing a bare date — so a 15-minute and a
 * 12-hour transfer created together read identically, and a 15-minute link created at 23:55 showed
 * TODAY's date while already being dead.
 *
 * @param expiresAt   the instant, in any form {@link DateLike} accepts
 * @param expireAfter the rung the sender chose; a null (unknown) retention is treated as day-based,
 *                    which is the precision every transfer had before the sub-day ladder existed
 */
export function formatExpiryPoint(
	expiresAt: DateLike,
	expireAfter: ExpiresAfter | null | undefined,
	locale: Locale,
): string {
	return expireAfter != null && isSubDayExpiry(expireAfter)
		? formatDateTimeMed(expiresAt, locale)
		: formatDate(expiresAt, locale);
}
